import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import {
  DASHBOARD_FILTER_KEY_MAP,
  DashboardFiltersEntity,
} from 'types/store/normalized/dashboard/dashboardFilters';
import { formatDashboardFilters } from 'utils/functions/dashboard';
import {
  DashboardFilters,
  FormattedFilters,
} from 'utils/types/dashboardFilters';
import { insightFilterOptionsSelectors } from 'state/normalized/insightFilterOptions/insightFilterOptionsSlice';
import {
  getDashboardByIdWithRelationships,
  getDashboardByIdWithAllRelationships,
} from 'state/normalized/dashboard/dashboardSlice';
import { SLICE_STATUS } from 'utils/constants';
import { DASHBOARDS_LIST_ACTIONS } from 'utils/constants/dashboard';
import { Status } from 'utils/customTypes';

/* ============================== STATE SETUP =============================== */
interface DashboardFiltersState {
  statusById: Record<string, Status>;
}

const initialState: DashboardFiltersState = {
  statusById: {},
};

const dashboardFiltersAdapter = createEntityAdapter<DashboardFiltersEntity>();

/* ============================== REDUX THUNK =============================== */
export const updateDashboardFilters = createAsyncThunk<
  { dashboardId: string; filters: DashboardFilters },
  {
    dashboardId: string;
    filters?: DashboardFilters;
  },
  ThunkApi
>(
  'normalized/dashboardFilters/updateDashboardFilters',
  async (
    { dashboardId, filters },
    { getState, extra: { getDashboardApi } }
  ) => {
    const api = getDashboardApi();
    const affordances =
      getState().normalized.dashboard.affordances.resources[dashboardId];
    const filtersUrl = affordances?.[DASHBOARDS_LIST_ACTIONS.FILTERS] ?? '';

    if (filters && Object.keys(filters).length > 0) {
      const savedFilters = await api.saveDashboardFilters(filtersUrl, filters);
      return { dashboardId, filters: savedFilters };
    }

    await api.deleteDashboardFilters(filtersUrl);
    return { dashboardId, filters: {} };
  }
);

export const restoreDashboardFilters = createAsyncThunk<
  { dashboardId: string; filters: DashboardFilters },
  { dashboardId: string },
  ThunkApi
>(
  'normalized/dashboardFilters/restoreDashboardFilters',
  async ({ dashboardId }, { extra: { getDashboardApi } }) => {
    const api = getDashboardApi();
    const filters = await api.getDashboardFilters(dashboardId);
    return { dashboardId, filters };
  }
);

/* ================================= REDUCER ================================ */
const dashboardFiltersSlice = createSlice({
  name: 'normalized/dashboardFilters',
  initialState: dashboardFiltersAdapter.getInitialState(initialState),
  reducers: {
    applyLocalFilters: (
      state,
      action: PayloadAction<{ dashboardId: string; filters: DashboardFilters }>
    ) => {
      const { dashboardId, filters } = action.payload;
      dashboardFiltersAdapter.setOne(state, {
        id: dashboardId,
        appliedFilters: filters,
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardByIdWithRelationships.fulfilled, (state, action) => {
        if (action.payload.filters) {
          dashboardFiltersAdapter.setOne(state, {
            id: action.payload.dashboard.id,
            appliedFilters: action.payload.filters,
          });
        }
      })
      .addCase(
        getDashboardByIdWithAllRelationships.pending,
        (state, action) => {
          state.statusById[action.meta.arg.dashboardId] = SLICE_STATUS.LOADING;
        }
      )
      .addCase(
        getDashboardByIdWithAllRelationships.fulfilled,
        (state, action) => {
          state.statusById[action.payload.dashboard.id] = SLICE_STATUS.SUCCESS;
          if (action.payload.filters) {
            dashboardFiltersAdapter.setOne(state, {
              id: action.payload.dashboard.id,
              appliedFilters: action.payload.filters,
            });
          }
        }
      )
      .addCase(updateDashboardFilters.pending, (state, action) => {
        state.statusById[action.meta.arg.dashboardId] = SLICE_STATUS.LOADING;
      })
      .addCase(updateDashboardFilters.fulfilled, (state, action) => {
        const { dashboardId, filters } = action.payload;
        state.statusById[dashboardId] = SLICE_STATUS.SUCCESS;
        dashboardFiltersAdapter.setOne(state, {
          id: dashboardId,
          appliedFilters: filters,
        });
      })
      .addCase(restoreDashboardFilters.fulfilled, (state, action) => {
        const { dashboardId, filters } = action.payload;
        dashboardFiltersAdapter.setOne(state, {
          id: dashboardId,
          appliedFilters: filters,
        });
      });
  },
});

/* =============================== SELECTORS ================================ */
const defaultSelectors = dashboardFiltersAdapter.getSelectors<RootState>(
  (state) => state.normalized.dashboardFilters
);

const selectStatusById = (state: RootState, dashboardId: string): Status =>
  state.normalized.dashboardFilters.statusById[dashboardId];

const selectAppliedFiltersById = createSelector(
  [defaultSelectors.selectById],
  (entity): DashboardFilters => entity?.appliedFilters ?? {}
);

const selectFormattedAppliedFiltersById = createSelector(
  [
    selectAppliedFiltersById,
    insightFilterOptionsSelectors.selectLearningTeamsOptions,
  ],
  (appliedFilters, learningTeamsOptions): Array<FormattedFilters> => {
    return formatDashboardFilters(appliedFilters, { learningTeamsOptions });
  }
);

const selectAppliedFiltersForRequestById = createSelector(
  [selectAppliedFiltersById],
  (appliedFilters): Record<string, string> => {
    type FilterKey = keyof DashboardFilters;
    type Value = string | undefined;

    return (
      Object.entries(appliedFilters ?? {}) as [FilterKey, Value][]
    ).reduce<Record<string, string>>((acc, [key, value]) => {
      if (!value) return acc;
      const queryKey = DASHBOARD_FILTER_KEY_MAP[key];
      acc[queryKey] = value;
      return acc;
    }, {});
  }
);

/* ================================ ACTIONS ================================= */
export const { applyLocalFilters } = dashboardFiltersSlice.actions;

/* ================================ EXPORTS ================================= */
export const dashboardFiltersSelectors = {
  ...defaultSelectors,
  selectStatusById,
  selectAppliedFiltersById,
  selectFormattedAppliedFiltersById,
  selectAppliedFiltersForRequestById,
};

export default dashboardFiltersSlice.reducer;
